import { DataVoyagerWidgetData } from '@/models/PanelWidgets';
import { getCurrentThread } from '@/store/reducers/UIState';
import { getDataVoyagerStateForThread } from '@/store/reducers/DataVoyagerWidgetStore';
import { LoadingSpinnerLogo } from '@/components/system/LoadingSpinnerLogo';
import { scrollToBottom, useDebounce } from '@/hooks/browserHooks';
import { Speaker } from '@/components/data-voyager/Speaker';
import { useAppSelector } from '@/store/hooks';

import { Link, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import Markdown from 'markdown-to-jsx';
import styled from 'styled-components';

export const DataVoyager = () => {
  const currentThread = useAppSelector(getCurrentThread);
  const dvData: DataVoyagerWidgetData | null = useAppSelector((state) =>
    getDataVoyagerStateForThread(state, currentThread?.id),
  );
  const widgetRef = useRef<HTMLDivElement>(null);
  const debouncedScrollToBottom = useDebounce(scrollToBottom, 500);

  // TODO: we can get fancier with disabling this when the user scrolls up in the future
  useEffect(() => {
    if (widgetRef.current && !!dvData) {
      debouncedScrollToBottom(widgetRef.current);
    }
  }, [dvData, widgetRef.current]);

  return (
    <Wrapper ref={widgetRef}>
      {!dvData?.logs && <LoadingSpinnerLogo />}
      <Markdown
        options={{
          overrides: {
            h3: {
              component: (props) => {
                const { id, ...rest } = props; // eslint-disable-line react/prop-types
                return <Speaker speakerId={id} {...rest} />;
              },
            },
            li: {
              component: (props) => <StyledText {...props} variant="body1" />,
            },
            pre: {
              component: (props) => <StyledCode {...props} />,
            },
            p: {
              component: (props) => <StyledText {...props} />,
              props: { paragraph: true, variant: 'body1' },
            },
            a: {
              component: (props) => <StyledLink {...props} />,
              props: {
                target: '_blank',
                paragraph: true,
                variant: 'body1',
              },
            },
            img: {
              component: (props) => <StyledImage {...props} />,
            },
          },
        }}
      >
        {dvData?.logs ?? ''}
      </Markdown>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(3)};
`;

const StyledCode = styled.pre`
  background-color: #000000;
  padding: ${({ theme }) => theme.spacing(1)};

  code {
    background-color: #000000;
    color: ${({ theme }) => theme.color['cream-100'].hex};
  }
`;

const StyledText = styled(Typography)`
  color: ${({ theme }) => theme.color['cream-100'].hex};
`;

const StyledLink = styled(Link)`
  &.MuiLink-root {
    color: ${({ theme }) => theme.palette.secondary.main};

    &:hover {
      color: ${({ theme }) => theme.palette.secondary.light};
    }
  }
`;

const StyledImage = styled.img`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  max-width: 1500px;
  width: 100%;
`;
